import React from 'react'
import { Space, Box, TextBody } from '@ally/metronome-ui'
import { AccountLOB } from '@ally/data-types-afg-accounts'
import { useCustomizeDashboard } from '../CustomizeDashboardContext/CustomizeDashboardContext'
import ReorderList from '../ReorderList'
import { DataTypeName, DataType } from '../types'
import { useHostServices } from '../../HostServices'
import { reorder } from '../utils'
import { useAccountTypes } from '../../AccountTypeContext'

const AccountGroups: React.FC<{
  onListChange: (type: DataTypeName, list: DataType<DataTypeName>[]) => void
}> = ({ onListChange }) => {
  const { hasActiveDepositAccounts, isDebtPlanUser } = useAccountTypes()
  const { featureFlags } = useHostServices()
  const [{ customizeApiData, customizeDataToPatch }] = useCustomizeDashboard()

  let lobs = customizeDataToPatch.lobs ?? customizeApiData?.lobs ?? []

  /*
  DEPOSIT is the only LOB that can "fall off" due to closed accounts after 60 days.
  So if the customize data saved has DEPOSIT and they have zero active accounts
  we filter out that LOB and reorder.
  */
  if (lobs.some(lob => lob.name === 'DEPOSIT') && !hasActiveDepositAccounts) {
    lobs = lobs.filter(lob => lob.name !== AccountLOB.DEPOSIT).map(reorder)
  }

  const isAutoLoanEnabled = featureFlags.variation('FF_SPOG_WEB_UI', false)
  const isCreditCardEnabled = featureFlags.variation(
    'FF_credit-card-snapshot',
    false,
  )

  /*
  if FF is off and they have auto accounts, filter out the AUTO LOB to hide
  it in the UI
  */
  if (!isAutoLoanEnabled) {
    lobs = lobs.filter(lob => lob.name !== AccountLOB.AUTO).map(reorder)
  }

  /*
  if FF is off, filter out the CC LOB to hide it in the UI
  */
  if (!isCreditCardEnabled) {
    lobs = lobs.filter(lob => lob.name !== AccountLOB.CC).map(reorder)
  }

  const getCaption = (): string => {
    if (isDebtPlanUser) {
      return 'How would you like to order your account groups and plans?'
    }
    return 'How would you like to order your account groups?'
  }

  return (
    <>
      {lobs.length > 1 ? (
        <Box mb="lg">
          <Space mb="sm">
            <TextBody tag="p" size="sm" weight="bold">
              {getCaption()}
            </TextBody>
          </Space>
          <Box>
            <ReorderList type="lobs" list={lobs} onListChange={onListChange} />
          </Box>
        </Box>
      ) : null}
    </>
  )
}

export default AccountGroups
